import type { SvgIconProps } from "@mui/material";
import { Badge, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useRef } from "react";

import type { ReportType } from "../../../../../../../api/types/svTypes.ts";

interface Props {
  reportType: ReportType;
  selectedReportType: ReportType;
  isReportChanged: boolean;
  tooltipText: string;
  editDisabled: boolean;
  onSelected: (newType: ReportType, ref: HTMLButtonElement | null) => void;
  ReactTypeIcon: React.ComponentType<SvgIconProps>;
}

const ReportTypeBadge = ({
  reportType,
  isReportChanged,
  onSelected,
  tooltipText,
  ReactTypeIcon,
  selectedReportType,
  editDisabled,
}: Props) => {
  const elementRef = useRef<HTMLButtonElement>(null);
  const selected = reportType === selectedReportType;

  return (
    <CustomBadge show={selected && isReportChanged} editDisabled={editDisabled}>
      <Tooltip arrow title={getToolTipText(tooltipText, selected && isReportChanged)}>
        <IconButton
          ref={elementRef}
          sx={{ opacity: editDisabled ? 0.5 : 1, borderRadius: "unset", svg: { fill: "none" } }}
          onClick={() => onSelected(reportType, elementRef.current)}
          disabled={editDisabled}
        >
          <ReactTypeIcon
            sx={(theme) => ({ path: { fill: selected ? theme.palette.primary.main : theme.palette.secondary.light } })}
          />
        </IconButton>
      </Tooltip>
    </CustomBadge>
  );
};

const CustomBadge = ({
  show,
  children,
  editDisabled,
}: React.PropsWithChildren<{ show: boolean; editDisabled: boolean }>) => {
  if (show === false) return <>{children}</>;
  return (
    <Badge
      variant="dot"
      color="primary"
      sx={{
        ".BaseBadge-badge": {
          height: "6px",
          minWidth: "6px",
          transform: "unset",
        },
        opacity: editDisabled ? 0.5 : 1,
      }}
    >
      {children}
    </Badge>
  );
};

const getToolTipText = (text: string, selectedAndChanged: boolean) => {
  if (selectedAndChanged) {
    return (
      <Stack>
        <Typography variant="caption">{text}</Typography>
        <Typography variant="caption">* has unsaved changes</Typography>
      </Stack>
    );
  }
  return <Typography variant="caption">{text}</Typography>;
};

export default ReportTypeBadge;
